import {
  INCIDENT_STATUSES,
  type ClosedReason,
  type IncidentEventActor,
  type IncidentEventType,
  type IncidentHistoryEntryResponse,
  type IncidentStatus,
} from '@prerender/diagnostics-api';

import { crawlerLabel } from './crawlerLabels';
import { INCIDENT_STATUS_LABELS } from './incidentStatusLabels';

export const INCIDENT_EVENT_ACTORS = ['system', 'cs'] as const satisfies readonly IncidentEventActor[];

const ACTOR_LABELS: Record<IncidentEventActor, string> = {
  system: 'System',
  cs: 'CS',
};

const EVENT_TYPE_LABELS: Record<IncidentEventType, string> = {
  created: 'Created',
  domain_joined: 'Domain joined',
  domain_no_longer_affected: 'Domain no longer affected',
  domain_affected_again: 'Domain affected again',
  status_change: 'Status change',
  severity_change: 'Severity change',
  inconclusive: 'Could not be judged',
};

type DomainEventType = Extract<
  IncidentEventType,
  'domain_joined' | 'domain_no_longer_affected' | 'domain_affected_again' | 'inconclusive'
>;

const CRAWLER_DOMAIN_EVENT_VERBS: Record<DomainEventType, string> = {
  domain_joined: 'joined on',
  domain_no_longer_affected: 'no longer affected on',
  domain_affected_again: 'affected again on',
  inconclusive: 'could not be judged on',
};

const DOMAIN_EVENT_HEADS: Record<DomainEventType, string> = {
  domain_joined: 'Joined on',
  domain_no_longer_affected: 'No longer affected:',
  domain_affected_again: 'Affected again:',
  inconclusive: 'Could not be judged on',
};

function isDomainEvent(eventType: IncidentEventType): eventType is DomainEventType {
  return Object.hasOwn(CRAWLER_DOMAIN_EVENT_VERBS, eventType);
}

const DETECTED = 'Detected';

export const HISTORY_STATUSES = [
  ...INCIDENT_STATUSES,
  'checked_by_prerender',
  'closed_by_cs',
  'account_not_paying',
] as const satisfies readonly (IncidentStatus | ClosedReason)[];

export type HistoryStatus = typeof HISTORY_STATUSES[number];

const STATUS_WORDING: Record<HistoryStatus, string> = {
  ...INCIDENT_STATUS_LABELS,
  checked_by_prerender: 'Recovered',
  closed_by_cs: 'Resolved',
  account_not_paying: 'Account no longer paying',
};

export function isHistoryStatus(value: string): value is HistoryStatus {
  return Object.hasOwn(STATUS_WORDING, value);
}

export function actorLabel(actor: IncidentEventActor): string {
  return ACTOR_LABELS[actor];
}

export function eventTypeLabel(eventType: IncidentEventType): string {
  return EVENT_TYPE_LABELS[eventType];
}

export function crawlerOfEntry(detail: Record<string, unknown>): string | null {
  const crawler = detail.crawler;

  return typeof crawler === 'string' && crawler.length > 0 ? crawler : null;
}

export function historyChangeHeadLabel(entry: Pick<IncidentHistoryEntryResponse, 'eventType' | 'detail'>): string {
  const crawler = crawlerOfEntry(entry.detail);

  if (entry.eventType === 'created') {
    return crawler === null ? `${DETECTED} on` : `${crawlerLabel(crawler)} on`;
  }

  if (!isDomainEvent(entry.eventType)) {
    return `${eventTypeLabel(entry.eventType)} —`;
  }

  return crawler === null
    ? DOMAIN_EVENT_HEADS[entry.eventType]
    : `${crawlerLabel(crawler)} ${CRAWLER_DOMAIN_EVENT_VERBS[entry.eventType]}`;
}

export function historyChangeLabel(
  entry: Pick<IncidentHistoryEntryResponse, 'eventType' | 'domains' | 'detail'>
): string {
  if (entry.domains === null) {
    return entry.eventType === 'created' ? detectedWithoutDomainsLabel(entry.detail) : eventTypeLabel(entry.eventType);
  }

  return `${historyChangeHeadLabel(entry)} ${domainListLabel(entry.domains)}`;
}

function detectedWithoutDomainsLabel(detail: Record<string, unknown>): string {
  const crawler = crawlerOfEntry(detail);

  return crawler === null ? DETECTED : crawlerLabel(crawler);
}

export function domainListLabel(domains: readonly string[]): string {
  if (domains.length <= 1) {
    return domains.join('');
  }

  return `${domains.slice(0, -1).join(', ')} and ${domains[domains.length - 1]}`;
}

type ListedHistoryEntry = Pick<IncidentHistoryEntryResponse, 'eventType' | 'domainCount' | 'detail'> & {
  domains: readonly string[];
};

const MAX_INLINE_DOMAINS = 3;

export function listsInline(entry: ListedHistoryEntry): boolean {
  return (entry.domainCount ?? entry.domains.length) <= MAX_INLINE_DOMAINS;
}

export function domainCountLabel(entry: ListedHistoryEntry): string {
  return `${entry.domainCount ?? entry.domains.length} domains`;
}

export function historyRemainderLabel(
  entry: Pick<IncidentHistoryEntryResponse, 'domains' | 'domainCount'>
): string | null {
  if (entry.domains === null || entry.domainCount === null || entry.domainCount <= entry.domains.length) {
    return null;
  }

  return `…and ${entry.domainCount - entry.domains.length} more`;
}

export function historyStatusLabel(status: string): string {
  return isHistoryStatus(status) ? STATUS_WORDING[status] : status;
}

export function affectedDomainsLabel(count: number): string {
  if (count === 0) return 'No domain affected';

  return count === 1 ? '1 domain affected' : `${count} domains affected`;
}

export function historyAuthorLabel(actor: IncidentEventActor, detail: Record<string, unknown>): string {
  const by = detail.by;

  return typeof by === 'string' && by.length > 0 ? by : actorLabel(actor);
}
